import { P2GBox, P2GButton, P2GFade, P2GLink, P2GText } from '@parcel2go/core-ui';
import { useState } from 'react';
import useGoto from '~/hooks/use-goto';
import { RoutingConstants } from '../../lib/consts/RoutingConstants';
import OnboardingDashboardTestScenarios from '../onboarding/testing/test-scenarios';
import { OnboardingTitle } from '../onboarding/setup/screens/rules/onboarding-title';
import { SidePanelContent } from '~/components/sidepanel-section';
import { Trans, useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { SettingsCache } from '~/lib/consts/settings-cache';

interface props {
  children: React.ReactNode;
}

const NewSmartSendUserPrompt = ({ children }: props) => {
  const { goTo } = useGoto();
  const [skip, setSkip] = useState(false);
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);

  const onboardingIsEnabled = SettingsCache.settings.Features.includes('SetupPrompt');

  if (!onboardingIsEnabled) {
    return (
      <P2GFade in>
        <P2GBox display="flex" flexDirection="column" alignItems="center" gap={3} my={4}>
          <P2GBox textAlign="center">
            <SidePanelContent>
              <OnboardingTitle
                text="Welcome to <span>Smart Send</span>"
                translationKey="SOLD_ITEMS_SETUP_INTRO_TITLE"
              />
              <P2GText variant="subtitle1">
                <Trans
                  t={t}
                  i18nKey="SOLD_ITEMS_SETUP_INTRO_SUBTITLE"
                  defaults="Manage and ship all your eCommerce sales in one place.<br />Add your contact details so we can find the best delivery quotes."
                />
              </P2GText>
              <P2GText variant="caption" component="p" my={2}>
                <Trans
                  t={t}
                  i18nKey="SOLD_ITEMS_SETUP_INTRO_OLD_ORDERS_LINK"
                  defaults="If you are looking for your order history, <a>click here</a>"
                  components={{ a: <P2GLink onClick={() => goTo(RoutingConstants.OrderHistory)} /> }}
                />
              </P2GText>
            </SidePanelContent>
          </P2GBox>
          <P2GBox>{children}</P2GBox>
        </P2GBox>
      </P2GFade>
    );
  }

  return (
    <>
      <P2GFade in key={skip ? 'smartsend-new-user-prompt-skipped' : 'smartsend-new-user-prompt'}>
        <P2GBox display="flex" flexDirection="column" alignItems="center" gap={3} my={4}>
          {skip ? (
            <>
              <P2GBox textAlign="center">
                <SidePanelContent>
                  <OnboardingTitle
                    text="Add your collection <span>address</span>"
                    translationKey="SOLD_ITEMS_SETUP_INTRO_SKIPPED_TITLE"
                  />
                  <P2GText variant="subtitle1">
                    <Trans
                      t={t}
                      i18nKey="SOLD_ITEMS_SETUP_INTRO_SKIPPED_SUBTITLE"
                      defaults="You skipped the setup, but we still need your contact details<br />to arrange collections and find your nearest drop-off points."
                    />
                  </P2GText>
                </SidePanelContent>
              </P2GBox>
              <P2GBox>{children}</P2GBox>
            </>
          ) : (
            <>
              <P2GBox textAlign="center">
                <SidePanelContent>
                  <OnboardingTitle text="Welcome to <span>Smart Send</span>" translationKey="SOLD_ITEMS_INTRO_TITLE" />
                  <P2GText variant="subtitle1">
                    <Trans
                      t={t}
                      i18nKey="SOLD_ITEMS_INTRO_SUBTITLE"
                      defaults="Manage and ship all your eCommerce sales in one place.<br /><br />Let's link your accounts, configure smart automation rules,<br />and set up your preferred collection options."
                    />
                  </P2GText>
                  <P2GText variant="caption" component="p" my={2}>
                    <Trans
                      t={t}
                      i18nKey="SOLD_ITEMS_SETUP_INTRO_OLD_ORDERS_LINK"
                      defaults="If you are looking for your order history, <a>click here</a>"
                      components={{ a: <P2GLink onClick={() => goTo(RoutingConstants.OrderHistory)} /> }}
                    />
                  </P2GText>
                </SidePanelContent>
              </P2GBox>
              <P2GButton onClick={() => goTo(RoutingConstants.SmartSendSetup)} variant="primary" size="large">
                {t('SOLD_ITEMS_SETUP_GET_STARTED_BUTTON', { defaultValue: 'Get Started' })}
              </P2GButton>
              <P2GLink onClick={() => setSkip(true)}>
                {t('SKIP_DO_LATER_BUTTON', { defaultValue: "Skip, I'll do this later" })}
              </P2GLink>
            </>
          )}
        </P2GBox>
      </P2GFade>
      <OnboardingDashboardTestScenarios />
    </>
  );
};

export default NewSmartSendUserPrompt;
